import { detectKakaoBrowser } from './kakao-browser';

export type VideoSaveOutcome = 'saved' | 'shared' | 'downloaded' | 'cancelled';

interface WritableVideoFile {
  write(data: Blob): Promise<void>;
  close(): Promise<void>;
}

interface VideoFileHandle {
  createWritable(): Promise<WritableVideoFile>;
}

interface VideoSavePickerOptions {
  id: string;
  suggestedName: string;
  startIn: 'videos';
  types: Array<{
    description: string;
    accept: Record<string, string[]>;
  }>;
}

export interface VideoSavePlatform {
  readonly savePicker?: (options: VideoSavePickerOptions) => Promise<VideoFileHandle>;
  readonly prefersNativeShare: boolean;
  readonly shareBeforePicker?: boolean;
  readonly canShare?: (file: File) => boolean;
  readonly share?: (file: File) => Promise<void>;
  readonly download: (file: File) => void;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function browserVideoSavePlatform(): VideoSavePlatform {
  const kakao = detectKakaoBrowser(navigator.userAgent);
  const savePicker = (window as typeof window & {
    showSaveFilePicker?: (options: VideoSavePickerOptions) => Promise<VideoFileHandle>;
  }).showSaveFilePicker;

  return {
    // Kakao WebView can expose the picker even when the native write step does
    // not complete. Skip that path and prefer the OS share sheet instead.
    savePicker: !kakao.isKakao && savePicker ? (options) => savePicker.call(window, options) : undefined,
    prefersNativeShare: matchMedia('(max-width: 767px)').matches || navigator.maxTouchPoints > 1,
    shareBeforePicker: kakao.isKakao,
    canShare: navigator.canShare ? (file) => navigator.canShare({ files: [file] }) : undefined,
    share: navigator.share
      ? (file) => navigator.share({ title: '내 역마살 영상', files: [file] })
      : undefined,
    download: (file) => {
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      link.hidden = true;
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  };
}

async function shareVideoFile(
  file: File,
  platform: VideoSavePlatform,
): Promise<'shared' | 'cancelled' | null> {
  if (!platform.prefersNativeShare || !platform.canShare?.(file) || !platform.share) return null;
  try {
    await platform.share(file);
    return 'shared';
  } catch (error) {
    if (isAbortError(error)) return 'cancelled';
    return null;
  }
}

export async function saveVideoFile(
  file: File,
  platform: VideoSavePlatform = browserVideoSavePlatform(),
): Promise<VideoSaveOutcome> {
  if (platform.shareBeforePicker) {
    const shareOutcome = await shareVideoFile(file, platform);
    if (shareOutcome) return shareOutcome;
  }

  if (platform.savePicker) {
    try {
      const handle = await platform.savePicker({
        id: 'oneultool-timeline-video',
        suggestedName: file.name,
        startIn: 'videos',
        types: [{
          description: 'MP4 영상',
          accept: { 'video/mp4': ['.mp4'] },
        }],
      });
      const writable = await handle.createWritable();
      await writable.write(file);
      await writable.close();
      return 'saved';
    } catch (error) {
      if (isAbortError(error)) return 'cancelled';
      // Older embedded browsers can expose the picker but fail while writing.
      // Continue to native sharing or the regular browser download.
    }
  }

  if (!platform.shareBeforePicker) {
    const shareOutcome = await shareVideoFile(file, platform);
    if (shareOutcome) return shareOutcome;
  }

  platform.download(file);
  return 'downloaded';
}
